import { AsIsTableDataTransform, ColumnConfig, ColumnConfigOptions, ColumnText, CombinedPortfoliosMultiColumnGridExtractorConfig, ConditionalExtractor, DateFormatter, ExtractAndFormatExtractor, LabelFormatter, LodashExtractor, LodashSeriesEnricherMapper, NumberFormatter, StoreLodashExtractor, StoreNestedFilterExtractor, TabConfig, TablePerPortfolioSeriesExtractorConfig } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';

import { FAMILY_COLUMN_CONFIG, HOLDINGS_ENRICHER, MODEL_PORTFOLIO_FILTER_FUNCTION, MODEL_PORTFOLIO_FILTER_SELECTED_IDS } from './family-column.config';

export const FAMILY_LATEST_HOLDINGS_HEADER = new TabConfig(
	'latestHoldings.header',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyOverview', 'familyPerformance'],
			['families', 'familyOverview', 'familyPerformance'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					managementType: portfolio?.managementType || data?.families?.managementType,
					...data?.familyOverview?.models?.find(model => model?.id === portfolio.id || model?.id === portfolio.portfolioId) ?? []
				};
			},
			MODEL_PORTFOLIO_FILTER_FUNCTION
		),
		null,
		[
			ColumnConfig.create({
				id: 'portfolioId',
				extractor: new LodashExtractor('portfolioId', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'name',
				extractor: new ConditionalExtractor(
					model => model.managementType === 'research' && model.pkg !== 'ACT-LAM',
					new ExtractAndFormatExtractor('shortName', '', (description) => '*' + description),
					new LodashExtractor('shortName', '')
				),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'expenseRatio',
				extractor: new StoreNestedFilterExtractor(
					['familyOverview'],
					'characteristics',
					[
						{ propertyToCompare: 'name', comparedValue: 'expenseRatio', desiredValueExpression: 'total' }
					]
				),
				labelFormatter: new NumberFormatter(),
				visible: true,
				label: new ColumnText('NET_EXPENSE_RATIO', 'Net Expense Ratio (%)')
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'grossExpenseRatio',
				extractor: new StoreNestedFilterExtractor(
					['familyOverview'],
					'characteristics',
					[
						{ propertyToCompare: 'name', comparedValue: 'grossExpenseRatio', desiredValueExpression: 'total' }
					]
				),
				labelFormatter: new NumberFormatter(),
				visible: true,
				label: new ColumnText('GROSS_EXPENSE_RATIO', 'Gross Expense Ratio (%)')
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'ytdReturn',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[
						{ propertyToCompare: 'name', comparedValue: 'returnYtd', desiredValueExpression: 'metricValue' }
					]
				),
				labelFormatter: new NumberFormatter(),
				visible: true,
				label: new ColumnText('YTD_RETURN', 'YTD Return (%)')
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'estimatedRisk',
				extractor: new LodashExtractor('risk.total', ''),
				labelFormatter: new NumberFormatter(),
				visible: true,
				label: new ColumnText('ESTIMATED_RISK', 'Estimated Risk (%)')
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'expenseRatioAsOfDate',
				extractor: new LodashExtractor('analysisDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'ytdReturnAsOfDate',
				extractor: new LodashExtractor('analysisDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'estimatedRiskAsOfDate',
				extractor: new LodashExtractor('analysisDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>)
		],
		FAMILY_COLUMN_CONFIG
	)
);

export const FAMILY_LATEST_HOLDINGS_TABLE_BODY = new TabConfig(
	'latestHoldings',
	null,
	new CombinedPortfoliosMultiColumnGridExtractorConfig(
		new LodashSeriesEnricherMapper(['familyHoldings', 'positionDetails'], ['familyHoldings', 'positionDetails'], 'familyHoldings.components', HOLDINGS_ENRICHER),
		null,
		new ColumnConfig(null, null, new StoreLodashExtractor(['familyHoldings'], 'id'), 'name'),
		new ColumnConfig(null, null, new LodashExtractor('posInfo.ticker.stringValue', ''), 'ticker'),
		new ColumnConfig(null, null, new LodashExtractor('weight', 0), 'weight', new NumberFormatter(NumberFormatter.NONE, 1, null, null, NumberFormatter.NONE, null, false, '-')),
		[
			new ColumnConfig(null, null, new LodashExtractor('posInfo.ticker.stringValue', ''), 'portfolioId'),
			new ColumnConfig(null, null, new LodashExtractor('posInfo.issueName.stringValue', ''), 'description'),
			new ColumnConfig(null, null, new LodashExtractor('posInfo.ticker.stringValue', ''), 'requestId'),
			new ColumnConfig(null, null, new LodashExtractor('formattedAcAssetClass', 'Other'), 'assetClass', new LabelFormatter('HOLDINGS_CHART')),
			new ColumnConfig(null, null, new LodashExtractor('changesToHoldings', ''), 'changes')
		],
		AsIsTableDataTransform.instance,
		Number.MAX_VALUE,
		port => MODEL_PORTFOLIO_FILTER_SELECTED_IDS(port[0])
	)
);

